import React, {
    useMemo,
    useCallback,
    useEffect,
    useState,
    Fragment,
} from 'react';

import { formatDateTime, getRelationshipToToday } from 'libs/utils';

import { ProfileData, supabase, useAuth } from 'hooks/useAuth';
import { useToast } from 'hooks/useToast';
import { ClosingData, GroupData } from 'hooks/useMemberGroupData';

import {
    filterBankedSessionDetails,
    JoinedGroupProgramHistory,
    JoinedSessionDetails,
    repeatableSessionDetailsQuery,
    sortSessionDetails,
} from 'queries/groups';

import { GroupLearnings } from 'components/layout/GroupLearnings';
import { ReorderSessions } from 'components/ui/ReorderSessions';
import { Tab, TabControls } from 'components/ui/TabControls';
import { Button } from 'components/ui/Button';

import { Fonts, spacing } from 'styles/theme';
import { Bumper, NestedCard } from 'styles/shared';

interface GroupDetailsProps {
    groupId?: string;
}

export const GroupDetails = ({ groupId }: GroupDetailsProps) => {
    const { userId, userProfile } = useAuth();
    const { showSuccessToast, showErrorToast } = useToast();

    const [selectedTab, setSelectedTab] = useState<
        'members' | 'sessions' | 'learnings'
    >('members');
    const [groupData, setGroupData] = useState<GroupData | null>(null);
    const [memberProfileData, setMemberProfileData] = useState<ProfileData[]>(
        []
    );
    const [groupProgramHistory, setGroupProgramHistory] =
        useState<JoinedGroupProgramHistory | null>(null);
    const [repeatableSessionDetails, setRepeatableSessionDetails] =
        useState<JoinedSessionDetails | null>(null);
    const [closings, setClosings] = useState<Array<ClosingData>>([]);
    const [isUpdatingSessionOrder, setIsUpdatingSessionOrder] =
        useState<boolean>(false);

    const getGroup = useCallback(async () => {
        const { data, error } = await supabase
            .from('groups')
            .select()
            .eq('gid', groupId ?? '')
            .single();

        if (error == null) {
            setGroupData(data);
        } else {
            showErrorToast(error.message);
        }
    }, [groupId, showErrorToast]);

    const getMemberProfiles = useCallback(async () => {
        const { data, error } = await supabase
            .from('profiles')
            .select()
            .overlaps('member_groups', [groupId ?? '']);

        if (error == null) {
            setMemberProfileData(data);
        } else {
            showErrorToast(error.message);
        }
    }, [groupId, showErrorToast]);

    const getGroupProgramHistory = useCallback(async () => {
        // Cannot reuse groupProgramHistoryQuery function here or refetch
        // breaks due to an undiagnosed caching issue
        const { data, error } = await supabase
            .from('group_program_history')
            .select(
                `*,
                        group_program_history_session_details!inner(*),
                        session_details(
                            *,
                            session_details_modules!left(*),
                            modules(*,
                                exercises_frameworks(*)
                            )
                        )
                    `
            )
            .eq('id', groupData?.group_program_id ?? '');

        if (error == null) {
            setGroupProgramHistory(data);
        } else {
            showErrorToast(error.message);
        }
    }, [groupData?.group_program_id, showErrorToast]);

    const getRepeatableSessionDetails = useCallback(async () => {
        const { data, error } = await repeatableSessionDetailsQuery;

        if (error == null) {
            setRepeatableSessionDetails(data);
        } else {
            showErrorToast(error.message);
        }
    }, [showErrorToast]);

    const getClosings = useCallback(async () => {
        const { data, error } = await supabase
            .from('closings')
            .select('*')
            .eq('group_id', groupId ?? '')
            .order('session_date', { ascending: false });

        if (error == null) {
            setClosings(data);
        } else {
            showErrorToast(error.message);
        }
    }, [groupId, showErrorToast]);

    useEffect(() => {
        if (userId !== '') {
            getGroup();
            getMemberProfiles();
            getClosings();
        }
    }, [getClosings, getGroup, getMemberProfiles, userId]);

    useEffect(() => {
        if (groupData?.group_program_id != null) {
            getGroupProgramHistory();
            getRepeatableSessionDetails();
        }
    }, [
        getRepeatableSessionDetails,
        getGroupProgramHistory,
        groupData?.group_program_id,
    ]);

    const sortedSessionDetails = useMemo(
        () => sortSessionDetails(groupProgramHistory),
        [groupProgramHistory]
    );

    const bankedSessionDetails = useMemo(
        () => filterBankedSessionDetails(groupProgramHistory),
        [groupProgramHistory]
    );

    const isNextSessionDatePast = useMemo(
        () =>
            groupData?.next_session_date == null ||
            getRelationshipToToday(new Date(groupData?.next_session_date)) ===
                'previous',
        [groupData?.next_session_date]
    );

    const passClosingLinkToClipboard = useCallback(
        (sessionNumber: number, sessionDate: string) => async () => {
            try {
                await navigator.clipboard.writeText(
                    `http://home.thegrand.world?modalContent=closing&sessionDate=${encodeURIComponent(
                        sessionDate
                    )}`
                );
                showSuccessToast(
                    `Copied member closing link for Session ${sessionNumber} to clipboard!`
                );
            } catch (error) {
                if (typeof error === 'string') {
                    showErrorToast(error.toUpperCase());
                } else if (error instanceof Error) {
                    showErrorToast(error.message);
                }
            }
        },
        [showErrorToast, showSuccessToast]
    );

    const onSaveSessionReorder = useCallback(
        async (
            updatedSessionDetails: JoinedSessionDetails,
            updatedBankedSessionDetails: JoinedSessionDetails
        ) => {
            if (isUpdatingSessionOrder) {
                return;
            }
            setIsUpdatingSessionOrder(true);

            const {
                data: groupProgramHistoryData,
                error: groupProgramHistoryError,
            } = await supabase
                .from('group_program_history')
                .insert({
                    program_title: groupProgramHistory?.[0]?.program_title,
                    group_id: groupId,
                    published_by: userProfile?.full_name,
                    publish_notes: 'Sessions reordered during recap',
                })
                .select();
            if (groupProgramHistoryError != null) {
                showErrorToast(groupProgramHistoryError.message);
                setIsUpdatingSessionOrder(false);
                return;
            }

            const { error: groupProgramHistorySessionDetailsError } =
                await supabase
                    .from('group_program_history_session_details')
                    .insert(
                        updatedSessionDetails
                            .map((sessionDetailsDatum, index) => ({
                                group_program_history_id:
                                    groupProgramHistoryData?.[0]?.id ?? '',
                                session_details_id: sessionDetailsDatum.id,
                                session_number: index + 1,
                                is_banked: false,
                            }))
                            .concat(
                                updatedBankedSessionDetails.map(
                                    (bankedSessionDetailsDatum, index) => ({
                                        group_program_history_id:
                                            groupProgramHistoryData?.[0]?.id ??
                                            '',
                                        session_details_id:
                                            bankedSessionDetailsDatum.id,
                                        session_number: index + 1,
                                        is_banked: true,
                                    })
                                )
                            )
                    );
            if (groupProgramHistorySessionDetailsError != null) {
                showErrorToast(groupProgramHistorySessionDetailsError.message);
                setIsUpdatingSessionOrder(false);
                return;
            }

            const { error: groupUpdateError } = await supabase
                .from('groups')
                .update({
                    group_program_id: groupProgramHistoryData?.[0]?.id,
                })
                .eq('gid', groupId ?? '');

            if (groupUpdateError == null) {
                showSuccessToast('Your group objectives have been updated!');
                await getGroup();
                await getGroupProgramHistory();
                await getRepeatableSessionDetails();
            } else {
                showErrorToast(groupUpdateError.message);
            }

            setIsUpdatingSessionOrder(false);
        },
        [
            getGroup,
            getGroupProgramHistory,
            getRepeatableSessionDetails,
            groupId,
            groupProgramHistory,
            isUpdatingSessionOrder,
            showErrorToast,
            showSuccessToast,
            userProfile?.full_name,
        ]
    );

    const groupDetailsTabs: Array<Tab> = useMemo(() => {
        const tabsToReturn = [
            {
                text: 'Member List',
                isSelected: selectedTab === 'members',
                onClick: () => setSelectedTab('members'),
            },
        ];

        if (sortedSessionDetails.length > 0) {
            tabsToReturn.push({
                text: 'Session Order',
                isSelected: selectedTab === 'sessions',
                onClick: () => setSelectedTab('sessions'),
            });
        }

        if (closings.length > 0) {
            tabsToReturn.push({
                text: 'Group Learnings',
                isSelected: selectedTab === 'learnings',
                onClick: () => setSelectedTab('learnings'),
            });
        }

        return tabsToReturn;
    }, [closings.length, selectedTab, sortedSessionDetails.length]);

    const nextSessionRelationship = useMemo(
        () =>
            groupData?.next_session_date == null
                ? 'none'
                : getRelationshipToToday(new Date(groupData.next_session_date)),
        [groupData?.next_session_date]
    );

    return (
        <>
            <Fonts.Heading5>{groupId}</Fonts.Heading5>
            <Bumper $minHeight={spacing.xSmall} />
            {groupData?.next_session_date == null ? null : (
                <div>
                    <Button
                        onClick={passClosingLinkToClipboard(
                            groupData.session_counter,
                            groupData.next_session_date
                        )}
                        $style="outline"
                        $isSmall
                    >
                        {`Copy ${
                            isNextSessionDatePast ? 'previous ' : ''
                        }member closing link for Session ${
                            groupData.session_counter
                        }`}
                    </Button>
                    <Bumper
                        $minHeight={
                            isNextSessionDatePast ||
                            groupData?.previous_session_date == null
                                ? spacing.medium
                                : spacing.xSmall
                        }
                    />
                </div>
            )}
            {isNextSessionDatePast ||
            groupData?.previous_session_date == null ? null : (
                <div>
                    <Button
                        onClick={passClosingLinkToClipboard(
                            groupData.session_counter - 1,
                            groupData.previous_session_date
                        )}
                        $style="outline"
                        $isSmall
                    >
                        {`Copy previous member closing link for Session ${
                            groupData.session_counter - 1
                        }`}
                    </Button>
                    <Bumper $minHeight={spacing.xSmall} />
                </div>
            )}
            <Bumper />
            <TabControls tabs={groupDetailsTabs} $isFlexStart />
            <Bumper />
            {selectedTab === 'members' ? (
                <div>
                    <Fonts.Heading6>{`Members`}</Fonts.Heading6>
                    <Bumper />
                    {memberProfileData?.map((member) => {
                        if (member.member_groups?.includes(groupId ?? ''))
                            return (
                                <Fragment key={member.id}>
                                    <Fonts.Medium key={member.id}>
                                        {`•`}&nbsp;&nbsp;
                                        {member.full_name ?? ''}
                                    </Fonts.Medium>
                                    <Bumper $minHeight={spacing.xxSmall} />
                                </Fragment>
                            );
                    })}
                </div>
            ) : null}
            {selectedTab === 'sessions' ? (
                <div>
                    {nextSessionRelationship === 'none' ? null : (
                        <>
                            <Fonts.Medium $isOpenSansSemiBold>{`${
                                nextSessionRelationship === 'upcoming'
                                    ? `Next`
                                    : nextSessionRelationship === 'current'
                                    ? `Today's`
                                    : 'Final'
                            } session date: ${formatDateTime(
                                new Date(groupData?.next_session_date ?? 0)
                            )}`}</Fonts.Medium>
                            <Bumper $minHeight={spacing.xxSmall} />
                        </>
                    )}
                    <Fonts.Medium>
                        {`Here is the session order for this group. If you notice that anything is incorrect in the session list, please make updates below using the drag and drop options.`}
                    </Fonts.Medium>
                    <Bumper />
                    <NestedCard>
                        <ReorderSessions
                            initialPoolItems={repeatableSessionDetails ?? []}
                            initialListItems={sortedSessionDetails}
                            initialBankedItems={bankedSessionDetails}
                            onSave={onSaveSessionReorder}
                            currentSessionNumber={
                                groupData?.session_counter ?? 0
                            }
                            shouldShowDisclaimer={false}
                        />
                    </NestedCard>
                </div>
            ) : null}
            {selectedTab === 'learnings' ? (
                <GroupLearnings
                    closings={closings}
                    sessionDetails={sortedSessionDetails}
                    memberProfiles={memberProfileData}
                    shouldShowCoachVisibility
                />
            ) : null}
        </>
    );
};
